import { urlIcone3d } from '../utils/iconesVeiculos'
import React, { useEffect, useState, useRef } from "react"
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet"
import L from "leaflet"
import axios from "axios"
import "leaflet/dist/leaflet.css"
import { useUI } from '../contexts/UIContext'
import { useAuth } from '../contexts/AuthContext'
import Toast, { ToastItem } from '../components/Toast'
import EventosModal from '../components/EventosModal'
import { vencimentoLicenciamento, diasAte } from "../utils/licenciamento"

const API = process.env.REACT_APP_API_URL || "http://localhost:3001/api"

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
})

// Filtros CSS calculados via otimizacao numerica (matrizes reais de
// grayscale/sepia/saturate/hue-rotate/brightness do navegador) para
// reproduzir com precisao cada cor da paleta do SeletorIconeAtivo.tsx
// sobre a base cinza neutra dos icones topdown. Erro medido: 0.0 (exato).
const FILTROS_COR_EXATOS: Record<string, string> = {
  '#94a3b8': 'grayscale(1) sepia(1) saturate(0.64) hue-rotate(175.6deg) brightness(0.90)',
  '#f1f5f9': 'grayscale(1) sepia(1) saturate(0.09) hue-rotate(169deg) brightness(1.37)',
  '#1e293b': 'grayscale(1) sepia(1) saturate(3.67) hue-rotate(188.9deg) brightness(0.23)',
  '#ef4444': 'grayscale(1) sepia(1) saturate(47.92) hue-rotate(333.6deg) brightness(0.94)',
  '#3b82f6': 'grayscale(1) sepia(1) saturate(39.44) hue-rotate(200.8deg) brightness(0.96)',
  '#22c55e': 'grayscale(1) sepia(1) saturate(3.43) hue-rotate(89.2deg) brightness(0.87)',
  '#eab308': 'grayscale(1) sepia(1) saturate(3.96) hue-rotate(7.4deg) brightness(1.00)',
  '#f97316': 'grayscale(1) sepia(1) saturate(28.18) hue-rotate(349.4deg) brightness(0.98)',
}
const filtroCorIcone = (hex?: string | null) => {
  if (!hex) return "none"
  return FILTROS_COR_EXATOS[hex.toLowerCase()] || "none"
}
const ICONES_NAO_RECOLORAVEIS = new Set(['ambulancia_top', 'picape_bombeiros_top'])

// Tamanho (px) do ícone 3D no mapa por tipo de veículo.
const TAMANHO_3D: Record<string, number> = { moto: 58, carro: 74, pickup: 80, van: 80, caminhao: 90, onibus: 98, cavalo: 84, carreta: 106, rodotrem: 150 }
const criarIconePersonalizado = (iconId: string, iconColor: string|null, direcao: number, velocidade: number, selecionado: boolean, ignicao = true) => {
  const tamanho = selecionado ? 48 : 40
  const filtro = ICONES_NAO_RECOLORAVEIS.has(iconId) ? 'none' : filtroCorIcone(iconColor)
  const emMovimento = velocidade > 0
  const ehTopDown = iconId.endsWith('_top')

  if (iconId.startsWith('v3d_')) {
    const tipo = iconId.slice(4)
    const tam = Math.round((TAMANHO_3D[tipo] || 84) * (selecionado ? 1.15 : 1))
    const url = urlIcone3d(iconId, iconColor, direcao)
    const efeitos = [!ignicao ? 'grayscale(0.85) opacity(0.75)' : '', selecionado ? 'drop-shadow(0 0 4px rgba(56,189,248,0.8))' : ''].filter(Boolean).join(' ') || 'none'
    const html = `<div style="width:${tam}px;height:${tam}px;"><img src="${url}" width="${tam}" height="${tam}" style="object-fit:contain;display:block;filter:${efeitos};" /></div>`
    return L.divIcon({ html, iconSize: [tam, tam], iconAnchor: [tam/2, tam/2], popupAnchor: [0, -(tam/2)], className: "" })
  }

  if (ehTopDown) {
    const html = `<div style="position:relative;width:${tamanho}px;height:${tamanho}px;${selecionado?"filter:drop-shadow(0 0 4px rgba(56,189,248,0.8));":""}">
      <img src="/vehicle-icons/${iconId}.png" width="${tamanho}" height="${tamanho}" style="filter:${filtro};object-fit:contain;display:block;transform:rotate(${direcao||0}deg);transform-origin:50% 50%;" />
    </div>`
    return L.divIcon({ html, iconSize: [tamanho, tamanho], iconAnchor: [tamanho/2, tamanho/2], popupAnchor: [0, -(tamanho/2)], className: "" })
  }

  const seta = emMovimento ? `<div style="position:absolute;top:-8px;left:50%;width:16px;height:16px;transform:translateX(-50%) rotate(${direcao||0}deg);transform-origin:50% 80%;z-index:2;">
    <svg viewBox="0 0 24 24" width="100%" height="100%"><path d="M12 2 L20 20 L12 16 L4 20 Z" fill="#38bdf8" stroke="#fff" stroke-width="1"/></svg>
  </div>` : ""
  const html = `<div style="position:relative;width:${tamanho}px;height:${tamanho}px;${selecionado?"filter:drop-shadow(0 0 4px rgba(56,189,248,0.8));":""}">
    ${seta}
    <img src="/vehicle-icons/${iconId}.png" width="${tamanho}" height="${tamanho}" style="filter:${filtro};object-fit:contain;display:block;" />
  </div>`
  return L.divIcon({ html, iconSize: [tamanho, tamanho], iconAnchor: [tamanho/2, tamanho/2], popupAnchor: [0, -(tamanho/2)], className: "" })
}
const criarIcone = (ignicao: boolean, velocidade: number, selecionado = false, iconId?: string|null, iconColor?: string|null, direcao = 0) => {
  if (iconId) return criarIconePersonalizado(iconId, iconColor||null, direcao, velocidade, selecionado, ignicao)
  const cor = !ignicao ? "#64748b" : velocidade > 0 ? "#22c55e" : "#f59e0b"
  const borda = selecionado ? "#fff" : "rgba(255,255,255,0.7)"
  const tamanho = selecionado ? 42 : 36
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${tamanho}" height="${tamanho+8}" viewBox="0 0 36 44">
    <defs><filter id="s"><feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="rgba(0,0,0,0.4)"/></filter></defs>
    <ellipse cx="18" cy="40" rx="8" ry="3" fill="rgba(0,0,0,0.25)"/>
    <path d="M18 2 C10 2 4 8 4 16 C4 26 18 40 18 40 C18 40 32 26 32 16 C32 8 26 2 18 2Z"
      fill="${cor}" filter="url(#s)" stroke="${borda}" stroke-width="${selecionado?2.5:1.5}"/>
    <circle cx="18" cy="16" r="7" fill="white" opacity="0.9"/>
    <text x="18" y="20" text-anchor="middle" font-size="10" font-weight="bold" fill="${cor}">
      ${velocidade > 0 ? "▶" : ignicao ? "■" : "●"}
    </text>
  </svg>`
  return L.divIcon({ html: svg, iconSize: [tamanho, tamanho+8], iconAnchor: [tamanho/2, tamanho+8], popupAnchor: [0, -(tamanho+8)], className: "" })
}

const criarIconeComLabel = (
  ignicao: boolean, velocidade: number, selecionado: boolean,
  iconId: string|null|undefined, iconColor: string|null|undefined, direcao: number,
  nome: string, placa: string|null, motoristaNome: string|null, mostrarLabel: boolean
) => {
  const baseIcone = criarIcone(ignicao, velocidade, selecionado, iconId, iconColor, direcao)
  if (!mostrarLabel || selecionado) return baseIcone
  const linhaPlaca = placa ? `<div style="font-size:10px;color:#94a3b8;line-height:1.3;">${placa}</div>` : ''
  const linhaMotorista = motoristaNome ? `<div style="font-size:10px;color:#38bdf8;line-height:1.3;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:130px;">👤 ${motoristaNome}</div>` : ''
  const label = `<div style="position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);background:rgba(13,27,46,0.92);border:1px solid #2d4166;border-radius:6px;padding:3px 8px;white-space:nowrap;pointer-events:none;z-index:9999;box-shadow:0 2px 8px rgba(0,0,0,0.5);"><div style="font-size:11px;font-weight:700;color:#f1f5f9;line-height:1.4;">${nome}</div>${linhaPlaca}${linhaMotorista}<div style="position:absolute;top:100%;left:50%;transform:translateX(-50%);width:0;height:0;border-left:5px solid transparent;border-right:5px solid transparent;border-top:5px solid #2d4166;"></div></div>`
  const baseOpts = (baseIcone as any).options || {}
  const baseHtml = baseOpts.html || ''
  return L.divIcon({
    html: `<div style="position:relative;">${label}${baseHtml}</div>`,
    iconSize: baseOpts.iconSize,
    iconAnchor: baseOpts.iconAnchor,
    popupAnchor: baseOpts.popupAnchor,
    className: ''
  })
}

const iconeUltimoSinal = () => L.divIcon({
  html: `<div style="width:16px;height:16px;background:#dc2626;border:2px solid white;border-radius:50%;box-shadow:0 0 6px rgba(220,38,38,0.8)"></div>`,
  iconSize: [16,16], iconAnchor: [8,8], className: ""
})

function CentralizarMapa({ posicao }: { posicao: [number,number]|null }) {
  const map = useMap()
  // Carregar Phosphor Icons
  useEffect(() => {
    if (!document.querySelector('#phosphor-bold')) {
      const link = document.createElement('link')
      link.id = 'phosphor-bold'
      link.rel = 'stylesheet'
      link.href = 'https://unpkg.com/@phosphor-icons/web@2.1.1/src/bold/style.css'
      document.head.appendChild(link)
    }
  }, [])

  useEffect(() => { if (posicao) map.flyTo(posicao, 16, { duration: 1.2 }) }, [posicao, map])
  return null
}

export default function Mapa() {
  const [ativos, setAtivos]           = useState<any[]>([])
  const [toasts, setToasts]           = useState<ToastItem[]>([])
  const ultimoAlertaRef               = useRef<string | null>(null)
  const [selecionado, setSelecionado] = useState<any>(null)
  const [centralizar, setCentralizar] = useState<[number,number]|null>(null)
  const [busca, setBusca]             = useState("")
  const [filtro, setFiltro]           = useState("todos")
  const { toast, confirm } = useUI()
  const { usuario } = useAuth()
  const [loading, setLoading]         = useState(true)
  const [rastrSelecionado, setRastrSelecionado] = useState<any[]>([])
  const [ultimoSinalPos, setUltimoSinalPos]     = useState<any>(null)
  const [showReplay, setShowReplay]             = useState(false)
  const [bloqueioStatus, setBloqueioStatus]      = useState<any>({})
  const [bloqueando, setBloqueando]             = useState(false)
  const [motoristas, setMotoristas]             = useState<any[]>([])
  const [vinculandoMotorista, setVinculandoMotorista] = useState(false)
  const [mostrarMotoristas, setMostrarMotoristas]     = useState(false)
  const [mostrarLabels, setMostrarLabels] = useState(true)
  const [mostrarOcultos, setMostrarOcultos] = useState(false)
  const [modalNavegacao, setModalNavegacao]           = useState<{tipo:string,url:string,label:string}|null>(null)
  const [modalEventos, setModalEventos] = useState<{id:string,nome:string}|null>(null)
  const [sidebarRecolhida, setSidebarRecolhida]       = useState(false)
  const [replayInicio, setReplayInicio]         = useState("")
  const [replayFim, setReplayFim]               = useState("")
  const [replayHoraInicio, setReplayHoraInicio] = useState("00:00")
  const [replayHoraFim, setReplayHoraFim]       = useState("23:59")
  const intervalRef = useRef<any>(null)

  const carregar = async () => {
    try {
      const r = await axios.get(`${API}/posicoes/ao-vivo`)
      setAtivos(r.data)
      // Atualizar selecionado automaticamente se popup estiver aberto
      setSelecionado((sel:any) => {
        if (!sel) return sel
        const atualizado = r.data.find((a:any) => a.id === sel.id)
        return atualizado || sel
      })
    } catch { setAtivos([]) } finally { setLoading(false) }
  }

  useEffect(() => {
    carregar()
    intervalRef.current = setInterval(carregar, 10000)
    return () => clearInterval(intervalRef.current)
  }, [])

  // Toast de alertas em tempo real — verifica novos alertas a cada 10s
  const verificarNovosAlertas = async () => {
    try {
      const r = await axios.get(`${API}/alertas`)
      const lista = r.data || []
      if (!lista.length) return

      if (ultimoAlertaRef.current === null) {
        // Primeira execução: só marca o mais recente como referência, sem disparar toasts retroativos
        ultimoAlertaRef.current = lista[0].criado_em
        return
      }

      const novos = lista.filter((a: any) => new Date(a.criado_em) > new Date(ultimoAlertaRef.current!))
      if (novos.length) {
        ultimoAlertaRef.current = lista[0].criado_em
        const novosToasts: ToastItem[] = novos.slice(0, 5).map((a: any) => ({
          id: a.id,
          type: a.nivel === 'critico' ? 'error' : a.nivel === 'alerta' ? 'warning' : 'info',
          message: `${a.ativo_nome || 'Veículo'}: ${a.titulo || a.descricao || a.tipo}`,
          duration: 6000,
          onClick: () => {
            const ativoAlvo = ativos.find((at: any) => at.id === a.ativo_id)
            if (ativoAlvo) {
              setSelecionado(ativoAlvo)
              if (ativoAlvo.latitude && ativoAlvo.longitude) {
                setCentralizar([parseFloat(ativoAlvo.latitude), parseFloat(ativoAlvo.longitude)])
              }
            }
            setToasts(prev => prev.filter(t => t.id !== a.id))
          },
        }))
        setToasts(prev => [...prev, ...novosToasts])
      }
    } catch {}
  }

  useEffect(() => {
    verificarNovosAlertas()
    const alertaInterval = setInterval(verificarNovosAlertas, 10000)
    return () => clearInterval(alertaInterval)
  }, [])

  const clicarAtivo = async (ativo: any) => {
    setSelecionado(ativo)
    setUltimoSinalPos(null)
    setShowReplay(false)
    if (ativo.latitude && ativo.longitude)
      setCentralizar([parseFloat(ativo.latitude), parseFloat(ativo.longitude)])
    // Buscar rastreadores vinculados
    try {
      const r = await axios.get(`${API}/ativos/${ativo.id}/rastreadores`)
      setRastrSelecionado(r.data)
    } catch { setRastrSelecionado([]) }
    // Buscar status de bloqueio
    try {
      const rb = await axios.get(`${API}/ativos/${ativo.id}/bloqueio-status`)
      setBloqueioStatus(rb.data)
    } catch { setBloqueioStatus({}) }
    // Carregar motoristas do cliente
    setMostrarMotoristas(false)
    if (ativo.cliente_id) {
      axios.get(`${API}/motoristas?cliente_id=${ativo.cliente_id}`)
        .then(r=>setMotoristas(r.data)).catch(()=>{})
    }
  }

  const mostrarUltimoSinal = async (rastreadorId: string) => {
    try {
      // Usar posição atual do ativo (última posição conhecida)
      if (selecionado?.latitude && selecionado?.longitude) {
        const lat = parseFloat(selecionado.latitude)
        const lng = parseFloat(selecionado.longitude)
        if (!isNaN(lat) && !isNaN(lng)) {
          setUltimoSinalPos({ lat, lng, hora: selecionado.ultima_posicao_hora, imei: rastreadorId })
          setCentralizar([lat, lng])
          return
        }
      }
      // Tentar buscar última posição do histórico recente
      const hoje = new Date()
      const ontem = new Date(hoje.getTime() - 24*60*60*1000)
      const r = await axios.get(`${API}/posicoes/${selecionado?.id}/historico`, {
        params: {
          inicio: ontem.toISOString(),
          fim: hoje.toISOString()
        }
      })
      if (r.data && r.data.length > 0) {
        const pos = r.data[r.data.length - 1]
        const lat = parseFloat(pos.latitude)
        const lng = parseFloat(pos.longitude)
        if (!isNaN(lat) && !isNaN(lng)) {
          setUltimoSinalPos({ lat, lng, hora: pos.data_hora, imei: rastreadorId })
          setCentralizar([lat, lng])
        }
      }
    } catch(e) { console.error(e) }
  }

  const ativosVisiveis = ativos.filter(a => mostrarOcultos || !a.oculto_no_mapa)
  const filtrados = ativosVisiveis.filter(a => {
    const q = busca.toLowerCase()
    const mb = !busca || a.nome?.toLowerCase().includes(q) || a.placa?.toLowerCase().includes(q)
    if (filtro === "ligados")    return mb && a.ignicao
    if (filtro === "desligados") return mb && !a.ignicao
    if (filtro === "movimento")  return mb && a.velocidade > 0
    return mb
  })

  const ligados    = ativosVisiveis.filter(a => a.ignicao).length
  const movimento  = ativosVisiveis.filter(a => a.velocidade > 0).length
  const desligados = ativosVisiveis.filter(a => !a.ignicao).length
  const centro: [number,number] = [-15.7801, -47.9292]

  const fmtHora = (h: string) => h ? new Date(h).toLocaleString("pt-BR") : "—"
  const fmtTempoParado = (desde: string | null) => {
    if (!desde) return null
    const ms = new Date().getTime() - new Date(desde).getTime()
    const min = Math.floor(ms / 60000)
    if (min < 60) return `${min}min`
    const horas = Math.floor(min / 60)
    if (horas < 24) return `${horas}h ${min % 60}min`
    const dias = Math.floor(horas / 24)
    return `${dias}d ${horas % 24}h`
  }
  const fmtKm = (n: any) => n && parseFloat(n) > 0 ? `${Math.round(parseFloat(n)).toLocaleString("pt-BR")} km` : "—"
  const fmtHoraMotor = (n: any) => {
    if (!n || parseFloat(n) <= 0) return "—"
    const h = Math.floor(parseFloat(n))
    const m = Math.floor((parseFloat(n)-h)*60)
    const s = Math.floor(((parseFloat(n)-h)*60-m)*60)
    return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`
  }

  const direcaoTexto = (graus: number) => {
    if (!graus && graus !== 0) return "—"
    const dirs = ["N","NE","L","SE","S","SO","O","NO"]
    return dirs[Math.round(graus/45) % 8]
  }

  return (
    <div style={{ display:"flex", height:"100%", overflow:"hidden" }}>
      {/* Painel esquerdo */}
      <div style={{ width:sidebarRecolhida?36:520, minWidth:sidebarRecolhida?36:520, background:"#1a2744", borderRight:"1px solid #2d4166", display:"flex", flexDirection:"column", overflow:"hidden", transition:"width 0.2s, min-width 0.2s" }}>
        <div style={{ padding:16, borderBottom:"1px solid #2d4166" }}>
          <div style={{ fontSize:15, fontWeight:700, marginBottom:12, color:"#f1f5f9" }}>Monitoramento ao vivo</div>
          <div style={{ display:"flex", gap:8, marginBottom:12 }}>
            {[{label:"Total",valor:ativosVisiveis.length,cor:"#94a3b8"},{label:"Ligados",valor:ligados,cor:"#22c55e"},
              {label:"Movimento",valor:movimento,cor:"#0ea5e9"},{label:"Parados",valor:desligados,cor:"#64748b"}].map((s,i)=>(
              <div key={i} style={{ flex:1, background:"#0d1b2e", borderRadius:8, padding:"6px 4px", textAlign:"center", border:"1px solid #2d4166" }}>
                <div style={{ fontSize:16, fontWeight:700, color:s.cor }}>{s.valor}</div>
                <div style={{ fontSize:9, color:"#475569", marginTop:1 }}>{s.label}</div>
              </div>
            ))}
          </div>
          {['admin','superadmin'].includes(usuario?.perfil||'') && ativos.some(a=>a.oculto_no_mapa) && (
            <button onClick={()=>setMostrarOcultos(p=>!p)}
              style={{ width:'100%', marginBottom:6, padding:"5px 8px", borderRadius:6, cursor:"pointer",
                fontFamily:"Inter", fontSize:10, border:"1px solid #f59e0b",
                background:mostrarOcultos?"#451a03":"transparent",
                color:mostrarOcultos?"#fbbf24":"#f59e0b" }}>
              <i className={`ph-bold ${mostrarOcultos?"ph-eye":"ph-eye-slash"}`} style={{fontSize:11,marginRight:4}}/>
              {mostrarOcultos?"Ocultar ativos invisíveis":"Mostrar ativos ocultos"}
              {` (${ativos.filter(a=>a.oculto_no_mapa).length})`}
            </button>
          )}
          <input placeholder="Buscar veículo ou placa..." value={busca} onChange={e=>setBusca(e.target.value)} style={{ marginBottom:8 }} />
          <div style={{ display:"flex", gap:4 }}>
            {[{id:"todos",l:"Todos"},{id:"ligados",l:"Ligados"},{id:"movimento",l:"Movimento"},{id:"desligados",l:"Parados"}].map(f=>(
              <button key={f.id} onClick={()=>setFiltro(f.id)}
                style={{ flex:1, padding:"5px 4px", borderRadius:6, cursor:"pointer", fontFamily:"Inter",
                  border:filtro===f.id?"1px solid #0ea5e9":"1px solid #2d4166",
                  background:filtro===f.id?"#0c2a4a":"transparent",
                  color:filtro===f.id?"#38bdf8":"#64748b", fontSize:10 }}>
                {f.l}
              </button>
            ))}
          </div>
        </div>

        <div style={{ flex:1, overflowY:"auto", display:sidebarRecolhida?"none":"block" }}>
          {/* Cabeçalho da grid */}
          <div style={{ display:"grid", gridTemplateColumns:"2.5fr 1.5fr 1.5fr 2fr",
            padding:"6px 10px", borderBottom:"1px solid #2d4166",
            background:"#0d1b2e", position:"sticky", top:0, zIndex:1 }}>
            {["ID/TAG · Placa · Status","Motorista","Conexão","Ações"].map(h=>(
              <div key={h} style={{ fontSize:9, color:"#475569", fontWeight:600, textTransform:"uppercase" }}>{h}</div>
            ))}
          </div>
          {loading ? (
            <div style={{ padding:24, textAlign:"center", color:"#475569" }}>Carregando...</div>
          ) : filtrados.length === 0 ? (
            <div style={{ padding:24, textAlign:"center", color:"#475569", fontSize:13 }}>
              {ativos.length === 0 ? "Nenhum veículo com posição registrada" : "Nenhum resultado"}
            </div>
          ) : filtrados.map((a:any) => {
            const bloqStatus = {status: a.bloqueio_status}
            // Tensão bateria veículo: tenta power (outros modelos) depois tensao_bateria (ST8300/io1)
            const tensaoCarroRaw = a.atributos?.power ?? a.tensao_bateria ?? null
            // Bateria rastreador: tenta batteryLevel (percentual direto J16) depois battery_level (Volts ST8300/io2)
            const batRastrRaw = a.atributos?.batteryLevel ?? a.battery_level ?? null
            const batRastr = batRastrRaw !== null
              ? (parseFloat(String(batRastrRaw)) <= 5
                  ? Math.min(100, Math.max(0, Math.round((parseFloat(String(batRastrRaw)) - 3.0) / 1.2 * 100)))
                  : Math.min(100, Math.max(0, Math.round(parseFloat(String(batRastrRaw))))))
              : null
            const temSinal = a.ultima_posicao_hora && (new Date().getTime()-new Date(a.ultima_posicao_hora).getTime())<300000
            return (
              <div key={a.id}
                style={{ borderBottom:"1px solid rgba(45,65,102,0.3)", cursor:"pointer",
                  background:selecionado?.id===a.id?"#1a2f50":"transparent",
                  borderLeft:selecionado?.id===a.id?"3px solid #0ea5e9":"3px solid transparent" }}>
                <div style={{ display:"grid", gridTemplateColumns:"2.5fr 1.5fr 1.5fr 2fr",
                  padding:"10px 10px 4px 10px", gap:6 }} onClick={()=>clicarAtivo(a)}>

                  {/* COL 1: ID/TAG, Placa, Status */}
                  <div>
                    <div style={{ fontWeight:700, fontSize:12, color:"#f1f5f9", lineHeight:1.3 }}>{a.nome}</div>
                    {a.placa && <div style={{ fontSize:10, color:"#64748b", fontFamily:"monospace" }}>{a.placa}</div>}
                    <div style={{ marginTop:3 }}>
                      <div style={{ marginBottom:2 }}>
                        <span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, whiteSpace:"nowrap",
                          background:!a.ignicao?"#1e293b":a.velocidade>0?"#14532d":"#451a03",
                          color:!a.ignicao?"#64748b":a.velocidade>0?"#4ade80":"#fbbf24" }}>
                          {!a.ignicao&&a.velocidade>0?"⚠️ Deslig/Mov":!a.ignicao?"● Desligado":a.velocidade>0?"▶ Ligado/Movimento":"■ Ligado/Parado"}
                        </span>
                        {a.velocidade>0 && <span style={{ fontSize:9, color:"#0ea5e9", fontWeight:700, marginLeft:4 }}>{Math.round(a.velocidade)} km/h</span>}
                      </div>
                      <div>
                        {bloqStatus.status==='bloqueado' && <span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:"#450a0a", color:"#f87171", whiteSpace:"nowrap" }}><i className="ph-bold ph-lock" style={{fontSize:9,marginRight:3}} aria-hidden="true"/>Bloqueado</span>}
                        {bloqStatus.status==='pendente' && <span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:"#451a03", color:"#fbbf24", whiteSpace:"nowrap" }}><i className="ph-bold ph-clock-countdown" style={{fontSize:9,marginRight:3}} aria-hidden="true"/>Pendente</span>}
                        {(!bloqStatus.status||bloqStatus.status==='desbloqueado') && <span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:"#0d2e1a", color:"#4ade80", whiteSpace:"nowrap" }}><i className="ph-bold ph-lock-open" style={{fontSize:9,marginRight:3}} aria-hidden="true"/>Liberado</span>}
                      </div>
                    </div>
                  </div>

                  {/* COL 2: Motorista */}
                  <div>
                    <div style={{ fontSize:11, color:"#f1f5f9", fontWeight:500 }}>
                      {a.motorista_nome || <span style={{ color:"#475569", fontSize:10 }}>Não identificado</span>}
                    </div>
                    {a.rfid_tag && <div style={{ fontSize:9, color:"#0ea5e9", marginTop:2 }}>🏷️ {a.rfid_tag}</div>}
                  </div>

                  {/* COL 3: Conexão */}
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:4 }}>
                      <span style={{ width:7, height:7, borderRadius:"50%", display:"inline-block", flexShrink:0,
                        background:temSinal?"#22c55e":"#dc2626" }} />
                      <span style={{ fontSize:9, color:temSinal?"#22c55e":"#dc2626" }}><i className={`ph-bold ${temSinal?"ph-wifi":"ph-wifi-slash"}`} style={{fontSize:10,marginRight:2}} aria-hidden="true"/>{temSinal?"Online":"Offline"}</span>
                    </div>
                    {a.ultima_posicao_hora && (
                      <div style={{ fontSize:9, color:"#475569", marginTop:2 }}>{fmtHora(a.ultima_posicao_hora)}</div>
                    )}
                    {(!a.velocidade || a.velocidade <= 2) && a.ultimo_movimento_em && (
                      <div style={{ fontSize:9, color:"#f59e0b", marginTop:2, display:"flex", alignItems:"center", gap:3 }}>
                        <i className="ph-bold ph-pause-circle" style={{fontSize:10}} aria-hidden="true"/>
                        Parado há {fmtTempoParado(a.ultimo_movimento_em)}
                      </div>
                    )}
                    {a.atributos?.sat && <div style={{ fontSize:9, color:"#334155" }}><i className="ph-bold ph-cell-signal-full" style={{fontSize:11,marginRight:2}} aria-hidden="true"/>{a.atributos.sat} sat</div>}
                  </div>

                  {/* COL 4: Ações */}
                  <div style={{ display:"flex", flexWrap:"wrap", gap:2 }} onClick={e=>e.stopPropagation()}>
                    <button title="Seguir no mapa" onClick={()=>clicarAtivo(a)}
                      style={{ background:"#1e3a6e",border:"1px solid #2d4166",borderRadius:5,color:"#38bdf8",cursor:"pointer",fontSize:13,padding:"3px 6px" }}><i className="ph-bold ph-crosshair-simple" style={{fontSize:14}} aria-hidden="true"/></button>
                    <button title="Ver trajetos" onClick={()=>{ clicarAtivo(a); setTimeout(()=>setShowReplay(true),300) }}
                      style={{ background:"#1e3a6e",border:"1px solid #2d4166",borderRadius:5,color:"#a78bfa",cursor:"pointer",fontSize:13,padding:"3px 6px" }}><i className="ph-bold ph-path" style={{fontSize:14}} aria-hidden="true"/></button>
                    <button title="Centralizar no mapa" onClick={()=>{ if(a.latitude)setCentralizar([parseFloat(a.latitude),parseFloat(a.longitude)]) }}
                      style={{ background:"#1e3a6e",border:"1px solid #2d4166",borderRadius:5,color:"#34d399",cursor:"pointer",fontSize:13,padding:"3px 6px" }}><i className="ph-bold ph-map-pin" style={{fontSize:14}} aria-hidden="true"/></button>
                    <button title={`Velocidade: ${Math.round(a.velocidade||0)} km/h | Direção: ${a.direcao!==undefined?direcaoTexto(a.direcao):'—'}`}
                      style={{ background:"#1e3a6e",border:"none",borderRadius:4,color:a.velocidade>0?"#0ea5e9":"#475569",cursor:"pointer",fontSize:10,padding:"2px 4px",minWidth:28 }}>
                      {a.velocidade>0?`${Math.round(a.velocidade)}↗`:"0↗"}
                    </button>
                    {tensaoCarroRaw!==null && (
                      <button title={`Bateria veículo: ${parseFloat(String(tensaoCarroRaw)).toFixed(1)}V`}
                        style={{ background:"#1e3a6e",border:"none",borderRadius:4,
                          color:parseFloat(String(tensaoCarroRaw))>12.4?"#22c55e":parseFloat(String(tensaoCarroRaw))>11.8?"#fbbf24":"#f87171",
                          cursor:"pointer",fontSize:11,padding:"2px 4px" }}><i className="ph-bold ph-battery-full" style={{fontSize:13}} aria-hidden="true"/></button>
                    )}
                    <button title={`Bateria rastreador: ${batRastr!==null?batRastr+'%':'N/A'}`}
                      style={{ background:"#1e3a6e",border:"none",borderRadius:4,
                        color:batRastr!==null?(batRastr>50?"#22c55e":batRastr>20?"#fbbf24":"#f87171"):"#475569",
                        cursor:"pointer",fontSize:10,padding:"2px 4px",minWidth:24 }}>
                      {batRastr!==null?`${batRastr}%`:"📶"}
                    </button>
                    <button title="Alertas do ativo" onClick={()=>clicarAtivo(a)}
                      style={{ background:"#1e3a6e",border:"1px solid #2d4166",borderRadius:5,color:"#fb923c",cursor:"pointer",fontSize:13,padding:"3px 6px" }}><i className="ph-bold ph-bell-ringing" style={{fontSize:14}} aria-hidden="true"/></button>
                    <button title="Ver eventos" onClick={()=>setModalEventos({id:a.id,nome:a.nome})}
                      style={{ background:"#1e3a6e",border:"1px solid #2d4166",borderRadius:5,color:"#22d3ee",cursor:"pointer",fontSize:13,padding:"3px 6px" }}><i className="ph-bold ph-clock-counter-clockwise" style={{fontSize:14}} aria-hidden="true"/></button>
                    <button title="Enviar comando GPRS" onClick={()=>clicarAtivo(a)}
                      style={{ background:"#1e3a6e",border:"1px solid #2d4166",borderRadius:5,color:"#818cf8",cursor:"pointer",fontSize:13,padding:"3px 6px" }}><i className="ph-bold ph-broadcast" style={{fontSize:14}} aria-hidden="true"/></button>
                    {['admin','superadmin'].includes(usuario?.perfil||'') && (
                      <button title={a.oculto_no_mapa?"Mostrar ativo no mapa":"Ocultar ativo no mapa"}
                        onClick={async()=>{
                          try {
                            const r=await axios.put(`${API}/ativos/${a.id}/ocultar`,{},{headers:{Authorization:`Bearer ${localStorage.getItem('moobi_token')}`}})
                            setAtivos(prev=>prev.map(x=>x.id===a.id?{...x,oculto_no_mapa:r.data.oculto_no_mapa}:x))
                            if(r.data.oculto_no_mapa) setSelecionado(null)
                          } catch{}
                        }}
                        style={{ background:"#1e3a6e",border:"1px solid #2d4166",borderRadius:5,
                          color:a.oculto_no_mapa?"#f59e0b":"#64748b",cursor:"pointer",fontSize:13,padding:"3px 6px" }}>
                        <i className={`ph-bold ${a.oculto_no_mapa?"ph-eye-slash":"ph-eye"}`} style={{fontSize:14}} aria-hidden="true"/>
                      </button>
                    )}
                  </div>
                </div>
                {/* Linha de endereço */}
                {a.endereco && (
                  <div style={{ padding:"0 10px 8px 10px", fontSize:10, color:"#f1f5f9", fontWeight:600, lineHeight:1.4 }}>
                    <i className="ph-bold ph-map-pin" style={{fontSize:10,marginRight:3}} aria-hidden="true"/>{a.endereco.length>90?a.endereco.substring(0,90)+"...":a.endereco}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {!sidebarRecolhida && <div style={{ padding:"8px 16px", borderTop:"1px solid #2d4166", fontSize:11, color:"#334155", display:"flex", justifyContent:"space-between" }}>
          <span>🔄 Atualiza a cada 10s</span>
          <button onClick={carregar} style={{ background:"none", border:"none", color:"#0ea5e9", cursor:"pointer", fontSize:11, fontFamily:"Inter" }}>Atualizar agora</button>
        </div>}
      </div>

      {/* Botão recolher sidebar */}
      <div style={{ position:"relative", width:0, zIndex:1000 }}>
        <button onClick={()=>setSidebarRecolhida((p:boolean)=>!p)}
          title={sidebarRecolhida?"Expandir lista":"Recolher lista"}
          style={{ position:"absolute", left:-14, top:"50%", transform:"translateY(-50%)",
            width:14, height:48, borderRadius:"0 8px 8px 0",
            border:"1px solid #2d4166", borderLeft:"none",
            background:"#1a2744", color:"#0ea5e9", cursor:"pointer",
            fontSize:10, display:"flex", alignItems:"center", justifyContent:"center",
            writingMode:"horizontal-tb", padding:0 }}>
          {sidebarRecolhida?"»":"«"}
        </button>
      </div>
      {/* Mapa */}
      <div style={{ flex:1, position:"relative" }}>
        <MapContainer center={centro} zoom={5} style={{ width:"100%", height:"100%" }} zoomControl={true}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />
          <CentralizarMapa posicao={centralizar} />

          {filtrados.map((a:any) => {
            if (!a.latitude || !a.longitude) return null
            const lat = parseFloat(a.latitude), lng = parseFloat(a.longitude)
            if (isNaN(lat)||isNaN(lng)) return null
            return (
              <Marker key={a.id} position={[lat,lng]}
                icon={criarIconeComLabel(a.ignicao, a.velocidade||0, selecionado?.id===a.id, a.icon_id, a.icon_color, a.direcao||0, a.nome, a.placa||null, a.motorista_nome||null, mostrarLabels)}
                eventHandlers={{ click: ()=>clicarAtivo(a) }}>
                <Popup>
                  <div style={{ minWidth:240, fontFamily:"Inter,sans-serif" }}>
                    <div style={{ fontWeight:700, fontSize:14, marginBottom:4, color:"#1a2744" }}>{a.nome}</div>
                    {a.placa && <div style={{ fontSize:11, color:"#64748b", marginBottom:8 }}>
                      {a.marca?`${a.marca} `:""}  {a.modelo_veiculo||""} · {a.placa}
                    </div>}
                    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:4, marginBottom:8 }}>
                      <div style={{ background:"#f1f5f9", borderRadius:6, padding:"4px 8px" }}>
                        <div style={{ fontSize:9, color:"#64748b" }}>Ignição</div>
                        <div style={{ fontSize:12, fontWeight:600, color:a.ignicao?"#16a34a":"#dc2626" }}>{a.ignicao?"● Ligado":"● Desl."}</div>
                      </div>
                      <div style={{ background:"#f1f5f9", borderRadius:6, padding:"4px 8px" }}>
                        <div style={{ fontSize:9, color:"#64748b" }}>Velocidade</div>
                        <div style={{ fontSize:12, fontWeight:600, color:"#0ea5e9" }}>{Math.round(a.velocidade||0)} km/h</div>
                      </div>
                      {a.direcao !== undefined && <div style={{ background:"#f1f5f9", borderRadius:6, padding:"4px 8px" }}>
                        <div style={{ fontSize:9, color:"#64748b" }}>Direção</div>
                        <div style={{ fontSize:12, fontWeight:600 }}>{direcaoTexto(a.direcao)}</div>
                      </div>}
                      {a.tensao_bateria && <div style={{ background:"#f1f5f9", borderRadius:6, padding:"4px 8px" }}>
                        <div style={{ fontSize:9, color:"#64748b" }}>Bateria</div>
                        <div style={{ fontSize:12, fontWeight:600, color:"#f59e0b" }}>{a.tensao_bateria}V</div>
                      </div>}
                    </div>
                    {a.endereco && <div style={{ fontSize:11, color:"#475569", marginBottom:6 }}>📍 {a.endereco}</div>}
                    {a.ultima_posicao_hora && <div style={{ fontSize:10, color:"#94a3b8", marginBottom:8 }}>🕐 {fmtHora(a.ultima_posicao_hora)}</div>}
                    <div style={{ display:"flex", gap:6 }}>
                      <button onClick={()=>window.open(`https://waze.com/ul?ll=${lat},${lng}&navigate=yes`,"_blank")}
                        style={{ flex:1, padding:"6px", borderRadius:6, border:"none", background:"#00c0ef", color:"#fff", cursor:"pointer", fontSize:11, fontWeight:600 }}><i className="ph-bold ph-navigation-arrow" style={{fontSize:13,marginRight:5}}/>Waze</button>
                      <button onClick={()=>window.open(`https://www.google.com/maps?q=${lat},${lng}`,"_blank")}
                        style={{ flex:1, padding:"6px", borderRadius:6, border:"none", background:"#4285f4", color:"#fff", cursor:"pointer", fontSize:11, fontWeight:600 }}><i className="ph-bold ph-map-trifold" style={{fontSize:13,marginRight:5}}/>Maps</button>
                    </div>
                  </div>
                </Popup>
              </Marker>
            )
          })}

          {/* Marcador último sinal */}
          {ultimoSinalPos && (
            <Marker position={[ultimoSinalPos.lat, ultimoSinalPos.lng]} icon={iconeUltimoSinal()}>
              <Popup>
                <div style={{ fontFamily:"Inter", fontSize:12 }}>
                  <div style={{ fontWeight:700, color:"#dc2626", marginBottom:4 }}>📍 Último sinal</div>
                  <div style={{ color:"#64748b" }}>🕐 {fmtHora(ultimoSinalPos.hora)}</div>
                </div>
              </Popup>
            </Marker>
          )}
        </MapContainer>

        {/* Painel detalhe rico */}
        {/* Modal Navegação */}
        {modalNavegacao && (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:2000,
            display:"flex", alignItems:"center", justifyContent:"center" }}
            onClick={()=>setModalNavegacao(null)}>
            <div style={{ background:"#1a2744", border:"1px solid #2d4166", borderRadius:16,
              padding:20, width:300, boxShadow:"0 20px 60px rgba(0,0,0,0.5)",
              animation:"slideUpPanel 0.2s ease" }}
              onClick={e=>e.stopPropagation()}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#f1f5f9" }}>
                  {modalNavegacao.tipo==='waze'
                    ? <><svg width="18" height="18" viewBox="0 0 40 40" style={{marginRight:6,verticalAlign:"middle"}}><rect width="40" height="40" rx="8" fill="#05c8f7"/><path d="M20 5C12 5 5 11 5 19c0 5 3 9 7 12l-1 4 5-2c1 0 3 1 4 1 8 0 15-6 15-15S28 5 20 5z" fill="#fff"/><circle cx="15" cy="18" r="2.5" fill="#333"/><circle cx="25" cy="18" r="2.5" fill="#333"/><path d="M15 23s2 3 5 3 5-3 5-3" stroke="#333" stroke-width="1.5" fill="none" stroke-linecap="round"/><circle cx="27" cy="10" r="3" fill="#ff4444"/></svg>Abrir no Waze</>
                    : <><svg width="18" height="18" viewBox="0 0 48 48" style={{marginRight:6,verticalAlign:"middle"}}><path d="M24 4C15 4 8 11 8 20c0 13 16 28 16 28s16-15 16-28C40 11 33 4 24 4z" fill="#ea4335"/><path d="M24 4C24 4 16 11 8 20h16V4z" fill="#34a853"/><path d="M8 20c0 6 4 11 8 14L24 20H8z" fill="#fbbc04"/><path d="M40 20c0 6-4 11-8 14L24 20h16z" fill="#4285f4"/><circle cx="24" cy="20" r="6" fill="#fff"/></svg>Google Maps</>
                  }
                </div>
                <button onClick={()=>setModalNavegacao(null)}
                  style={{ background:"none", border:"none", color:"#64748b", cursor:"pointer" }}>
                  <i className="ph-bold ph-x" style={{fontSize:14}}/>
                </button>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                <button onClick={()=>{ window.open(modalNavegacao.url,"_blank"); setModalNavegacao(null) }}
                  style={{ padding:"12px 16px", borderRadius:10, border:"1px solid #2d4166",
                    background:"#0d1b2e", color:"#f1f5f9", cursor:"pointer", fontSize:13,
                    fontWeight:600, fontFamily:"Inter", display:"flex", alignItems:"center", gap:10 }}>
                  <i className="ph-bold ph-map-trifold" style={{fontSize:18,color:"#0ea5e9"}}/>
                  Abrir no mapa
                </button>
                <button onClick={()=>{
                  const msg = `📍 ${selecionado?.nome}${selecionado?.placa?' ('+selecionado.placa+')':''}
${modalNavegacao.url}`
                  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`,"_blank")
                  setModalNavegacao(null)
                }} style={{ padding:"12px 16px", borderRadius:10, border:"1px solid #25d366",
                    background:"#0d1b2e", color:"#25d366", cursor:"pointer", fontSize:13,
                    fontWeight:600, fontFamily:"Inter", display:"flex", alignItems:"center", gap:10 }}>
                  <svg width="20" height="20" viewBox="0 0 48 48"><circle cx="24" cy="24" r="22" fill="#25d366"/><path d="M24 8C15 8 8 15 8 24c0 3 1 6 2 8l-2 8 8-2c2 1 5 2 8 2 9 0 16-7 16-16S33 8 24 8z" fill="#fff"/><path d="M18 16c-1 0-2 1-3 3 0 3 2 6 4 8s5 4 8 4c2-1 3-2 3-3v-2l-3-1-1 1-1 1c-1 0-3-1-4-2s-2-3-2-4l1-1 1-1-1-3z" fill="#25d366"/></svg>
                  Compartilhar no WhatsApp
                </button>
                <button onClick={()=>{
                  const url = modalNavegacao!.url
                  const el = document.createElement('textarea')
                  el.value = url
                  el.style.position = 'fixed'
                  el.style.opacity = '0'
                  document.body.appendChild(el)
                  el.focus()
                  el.select()
                  document.execCommand('copy')
                  document.body.removeChild(el)
                  toast('Link copiado!','success',2000)
                  setModalNavegacao(null)
                }} style={{ padding:"12px 16px", borderRadius:10, border:"1px solid #2d4166",
                    background:"#0d1b2e", color:"#94a3b8", cursor:"pointer", fontSize:13,
                    fontWeight:600, fontFamily:"Inter", display:"flex", alignItems:"center", gap:10 }}>
                  <i className="ph-bold ph-copy" style={{fontSize:18,color:"#94a3b8"}}/>
                  Copiar link
                </button>
              </div>
            </div>
          </div>
        )}

        {selecionado && (
          <div style={{ position:"absolute", bottom:20, right:16, zIndex:1000,
            background:"#1e2f4a", border:"1px solid #2d4166", borderRadius:16,
            width:340, boxShadow:"0 12px 40px rgba(0,0,0,0.6)",
            maxHeight:"calc(100vh - 60px)", overflowY:"auto",
            animation:"slideUpPanel 0.25s ease" }}>
          <style>{`@keyframes slideUpPanel{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`}</style>

            {/* Header neutro — cor de status fica só no texto, não no fundo */}
            <div style={{ background:"#162035",
              borderRadius:"12px 12px 0 0", padding:"12px 16px",
              borderBottom:"1px solid #2d4166" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:16, color:"#f1f5f9" }}>{selecionado.nome}</div>
                  <div style={{ fontSize:11, color:"#94a3b8", marginTop:2, display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                    {selecionado.placa && <span style={{ background:"rgba(0,0,0,0.3)", borderRadius:4, padding:"1px 6px", fontFamily:"monospace" }}>{selecionado.placa}</span>}
                    {selecionado.marca && <span>{selecionado.marca}</span>}
                    {selecionado.modelo_veiculo && <span>{selecionado.modelo_veiculo}</span>}
                    {selecionado.ano_fabricacao && <span>{selecionado.ano_fabricacao}</span>}
                  </div>
                </div>
                <button onClick={()=>{ setSelecionado(null); setUltimoSinalPos(null) }}
                  style={{ background:"rgba(0,0,0,0.3)", border:"none", color:"#94a3b8", cursor:"pointer", fontSize:16, borderRadius:6, padding:"2px 6px" }}><i className="ph-bold ph-x" style={{fontSize:13}}/></button>
              </div>
              <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:12, fontWeight:600,
                  color:!selecionado.ignicao&&selecionado.velocidade>0?"#f87171":selecionado.ignicao?selecionado.velocidade>0?"#4ade80":"#fbbf24":"#64748b" }}>
                  {selecionado.ignicao
                  ? selecionado.velocidade>0 ? "Ligado / Movimento" : "Ligado / Parado"
                  : selecionado.velocidade>0 ? "Desligado / Movimento" : "Desligado / Parado"}
                </span>
                {selecionado.velocidade > 0 && (
                  <span style={{ fontSize:13, fontWeight:700, color:"#0ea5e9" }}>
                    {Math.round(selecionado.velocidade)} km/h {selecionado.direcao!==undefined&&`· ${direcaoTexto(selecionado.direcao)}`}
                  </span>
                )}
              </div>
            </div>

            <div style={{ padding:"12px 16px" }}>

              {/* Motorista */}
              <div style={{ background:"#0d1b2e", borderRadius:8, padding:"8px 12px", marginBottom:10,
                border:"1px solid #2d4166" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <i className="ph-bold ph-user-circle" style={{fontSize:26,color:"#64748b"}} aria-hidden="true"/>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:10, color:"#475569" }}>Motorista</div>
                    <div style={{ fontSize:13, fontWeight:600, color:"#f1f5f9" }}>
                      {selecionado.motorista_nome || "Não identificado"}
                    </div>
                    {selecionado.rfid_tag && (
                      <div style={{ fontSize:10, color:"#0ea5e9" }}><i className="ph-bold ph-tag" style={{fontSize:11,marginRight:3}}/> RFID: {selecionado.rfid_tag}</div>
                    )}
                  </div>
                  <button onClick={()=>setMostrarMotoristas(p=>!p)}
                    style={{ background:"#1e3a6e", border:"1px solid #2d4166", borderRadius:6,
                      color:"#94a3b8", cursor:"pointer", fontSize:10, padding:"4px 8px", fontFamily:"Inter" }}>
                    {mostrarMotoristas ? <i className="ph-bold ph-x" style={{fontSize:12}}/> : <i className="ph-bold ph-pencil-simple" style={{fontSize:12}}/>}
                  </button>
                  {selecionado.motorista_id && !mostrarMotoristas && (
                    <button title="Remover motorista" onClick={async()=>{
                      try {
                        await axios.put(`${API}/ativos/${selecionado.id}`, { motorista_id: 'null' })
                        const r = await axios.get(`${API}/posicoes/ao-vivo`)
                        const atualizado = r.data.find((a:any)=>a.id===selecionado.id)
                        if (atualizado) setSelecionado(atualizado)
                      } catch { toast('Erro ao remover motorista','error') }
                    }} style={{ background:"transparent", border:"1px solid #dc2626", borderRadius:6,
                      color:"#f87171", cursor:"pointer", fontSize:10, padding:"4px 8px",
                      marginLeft:4, fontFamily:"Inter" }}>
                      <i className="ph-bold ph-user-minus" style={{fontSize:12}}/>
                    </button>
                  )}
                </div>
                {mostrarMotoristas && (
                  <div style={{ marginTop:8 }}>
                    <select onChange={async(e)=>{
                      const motId = e.target.value
                      setVinculandoMotorista(true)
                      try {
                        await axios.put(`${API}/ativos/${selecionado.id}`, { motorista_id: motId||'null' })
                        const r = await axios.get(`${API}/posicoes/ao-vivo`)
                        const atualizado = r.data.find((a:any)=>a.id===selecionado.id)
                        if (atualizado) setSelecionado(atualizado)
                        setMostrarMotoristas(false)
                      } catch { toast('Erro ao vincular motorista','error') }
                      finally { setVinculandoMotorista(false) }
                    }} style={{ width:"100%", fontSize:12, marginBottom:4 }}
                      defaultValue={selecionado.motorista_id||""}>
                      <option value="">— Sem motorista —</option>
                      {motoristas.map((m:any)=>(
                        <option key={m.id} value={m.id}>{m.nome}</option>
                      ))}
                    </select>
                    {vinculandoMotorista && <div style={{ fontSize:10, color:"#64748b" }}>Salvando...</div>}
                  </div>
                )}
              </div>

              {/* Info grid */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:6, marginBottom:10 }}>
                {[
                  { label:"Hodômetro", valor:fmtKm(selecionado.hodometro_atual) },
                  { label:"Hora motor", valor:fmtHoraMotor(selecionado.hora_motor) },
                  { label:"Sinal GPS", valor:selecionado.atributos?.sat?`${selecionado.atributos.sat} sat`:"—" },
                  { label:"Sinal GSM", valor:selecionado.atributos?.io4!=null?`${selecionado.atributos.io4}/10`:"—" },
                  { label:"Bateria veíc.", valor:selecionado.tensao_bateria?`${parseFloat(selecionado.tensao_bateria).toFixed(1)}V`:"—" },
                  { label:"Bat. rastreador", valor:(()=>{ const v=selecionado.atributos?.batteryLevel??selecionado.battery_level??null; if(v===null)return"—"; const f=parseFloat(String(v)); return f<=5?`${Math.round((f-3)/1.2*100)}%`:`${Math.round(f)}%` })() },
                ].filter(item=>item.valor!=="—").map((item,i)=>(
                  <div key={i} style={{ background:"#1e2f4a", borderRadius:8, padding:"8px 6px", textAlign:"center" }}>
                    <div style={{ fontSize:14, fontWeight:600, color:"#f1f5f9" }}>{item.valor}</div>
                    <div style={{ fontSize:9, color:"#5b6b82", marginTop:2 }}>{item.label}</div>
                  </div>
                ))}
              </div>

              {/* Posição */}
              {selecionado.latitude && (
                <div style={{ background:"#0d1b2e", borderRadius:8, padding:"6px 10px", marginBottom:8, fontSize:10 }}>
                  <span style={{ color:"#475569" }}><i className="ph-bold ph-map-pin" style={{fontSize:11,marginRight:3}}/> Posição: </span>
                  <span style={{ color:"#64748b", fontFamily:"monospace" }}>
                    {parseFloat(selecionado.latitude).toFixed(5)}, {parseFloat(selecionado.longitude).toFixed(5)}
                  </span>
                </div>
              )}

              {/* Endereço */}
              {selecionado.endereco && (
                <div style={{ fontSize:11, color:"#94a3b8", marginBottom:8, lineHeight:1.5,
                  background:"#0d1b2e", borderRadius:8, padding:"8px 10px" }}>
                  <i className="ph-bold ph-map-pin" style={{fontSize:11,marginRight:4,color:"#475569"}}/>{selecionado.endereco}
                </div>
              )}

              {/* Última atualização */}
              <div style={{ fontSize:10, color:"#334155", marginBottom:10, display:"flex", justifyContent:"space-between" }}>
                <span style={{color:"#94a3b8",display:"flex",alignItems:"center",gap:4}}><i className="ph-bold ph-clock" style={{fontSize:12}}/> <strong style={{color:"#f1f5f9"}}>Última atualização:</strong> {fmtHora(selecionado.ultima_posicao_hora)}</span>
                <span style={{ color:"#0ea5e9", cursor:"pointer" }} onClick={carregar}><i className="ph-bold ph-arrow-clockwise" style={{fontSize:12,marginRight:3}}/>Atualizar</span>
              </div>

              {/* Documentos vencendo */}
              {(() => {
                const docs = []
                const hoje = new Date()
                const lic = vencimentoLicenciamento(selecionado.venc_ipva_licenciamento, selecionado.mes_venc_ipva_licenciamento, selecionado.dia_venc_licenciamento, hoje)
                if (lic.venc) {   // ultimo pagamento + 1 ano (ou o mes cadastrado, sem data de pagamento)
                  const dias = diasAte(lic.venc, hoje)
                  if (dias <= 90) docs.push({label:"IPVA/Licen.", dias, vencido:dias<0})
                }
                if (selecionado.venc_seguro) {
                  const dias = Math.ceil((new Date(selecionado.venc_seguro).getTime()-hoje.getTime())/(1000*60*60*24))
                  if (dias <= 90) docs.push({label:"Seguro", dias, vencido:dias<0})
                }
                if (docs.length === 0) return null
                return (
                  <div style={{ background:"#451a03", border:"1px solid #f59e0b", borderRadius:8, padding:"8px 10px", marginBottom:8 }}>
                    <div style={{ fontSize:10, fontWeight:600, color:"#fbbf24", marginBottom:4 }}>⚠️ Documentos</div>
                    {docs.map((d,i)=>(
                      <div key={i} style={{ fontSize:11, color:d.vencido?"#f87171":"#fbbf24" }}>
                        {d.vencido?`🚨 ${d.label} VENCIDO`:`⏰ ${d.label} vence em ${d.dias}d`}
                      </div>
                    ))}
                  </div>
                )
              })()}

              {/* Rastreadores */}
              {rastrSelecionado.length > 0 && (
                <div style={{ marginBottom:10 }}>
                  <div style={{ fontSize:10, fontWeight:600, color:"#64748b", marginBottom:6, textTransform:"uppercase", letterSpacing:1 }}><i className="ph-bold ph-broadcast" style={{fontSize:11,marginRight:4}}/> Rastreadores</div>
                  {rastrSelecionado.map((r:any) => (
                    <div key={r.id} style={{ display:"flex", alignItems:"center", gap:8,
                      background:"#1e2f4a", borderRadius:8, padding:"8px 10px", marginBottom:4,
                      border:"1px solid #2d4166" }}>
                      <i className="ph-bold ph-broadcast" style={{fontSize:16, color:"#5b6b82", flexShrink:0}} aria-hidden="true"/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:11, fontWeight:600, color:"#f1f5f9" }}>{r.nome||r.imei}</div>
                        <div style={{ fontSize:9, color:"#5b6b82", fontFamily:"monospace", letterSpacing:"0.5px" }}>{r.imei}</div>
                      </div>
                      {r.principal && <span style={{ fontSize:9, color:"#4ade80", display:"flex", alignItems:"center", gap:3 }}><i className="ph-bold ph-check-circle" style={{fontSize:11}}/>Principal</span>}
                      <button onClick={()=>mostrarUltimoSinal(r.rastreador_id||r.id)}
                        title="Ver último sinal"
                        style={{ background:"transparent", border:"1px solid #2d4166", borderRadius:6, color:"#94a3b8", padding:"5px 7px", cursor:"pointer", display:"flex", alignItems:"center" }}>
                        <i className="ph-bold ph-map-pin-area" style={{fontSize:13}} aria-hidden="true"/>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Botões */}
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginBottom:8 }}>
                <button onClick={()=>selecionado.latitude&&setModalNavegacao({
                    tipo:'waze',
                    url:`https://waze.com/ul?ll=${selecionado.latitude},${selecionado.longitude}&navigate=yes`,
                    label:'Waze'
                  })}
                  style={{ padding:"9px 4px", borderRadius:8, border:"1px solid #2d4166", background:"#1e2f4a", color:"#cbd5e1", cursor:"pointer", fontSize:11, fontFamily:"Inter", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4 }}>
                  <i className="ph-bold ph-waveform" style={{fontSize:16}} aria-hidden="true"/>
                  Waze
                </button>
                <button onClick={()=>selecionado.latitude&&setModalNavegacao({
                    tipo:'gmaps',
                    url:`https://www.google.com/maps?q=${selecionado.latitude},${selecionado.longitude}`,
                    label:'Google Maps'
                  })}
                  style={{ padding:"9px 4px", borderRadius:8, border:"1px solid #2d4166", background:"#1e2f4a", color:"#cbd5e1", cursor:"pointer", fontSize:11, fontFamily:"Inter", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4 }}>
                  <i className="ph-bold ph-map-trifold" style={{fontSize:16}} aria-hidden="true"/>
                  Maps
                </button>
                <button onClick={()=>window.open(`https://maps.google.com/maps?layer=c&cbll=${selecionado.latitude},${selecionado.longitude}`,"_blank")}
                  style={{ padding:"9px 4px", borderRadius:8, border:"1px solid #2d4166", background:"#1e2f4a", color:"#cbd5e1", cursor:"pointer", fontSize:11, fontFamily:"Inter", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4 }}>
                  <i className="ph-bold ph-person-simple-walk" style={{fontSize:16}} aria-hidden="true"/>
                  Street view
                </button>
                <button onClick={()=>setShowReplay(p=>!p)}
                  style={{ padding:"9px 4px", borderRadius:8, border:`1px solid ${showReplay?"#38bdf8":"#2d4166"}`, background:showReplay?"#0c2a4a":"#1e2f4a", color:showReplay?"#38bdf8":"#cbd5e1", cursor:"pointer", fontSize:11, fontFamily:"Inter", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4 }}>
                  <i className="ph-bold ph-path" style={{fontSize:16}} aria-hidden="true"/>
                  {showReplay?"Fechar":"Replay"}
                </button>
              </div>

              {/* Status e Botões de Bloqueio */}
              {selecionado.suporta_bloqueio && (
                <div style={{marginTop:8}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                    background:"#1e2f4a",borderRadius:8,padding:"8px 12px",marginBottom:6,
                    border:"1px solid #2d4166"}}>
                    <div>
                      <div style={{fontSize:10,color:"#64748b"}}>Status bloqueio</div>
                      <div style={{fontSize:13,fontWeight:700,display:"flex",alignItems:"center",gap:5,
                        color:bloqueioStatus.status==='bloqueado'?"#f87171":bloqueioStatus.status==='pendente'?"#fbbf24":"#22c55e"}}>
                        
                        {bloqueioStatus.status==='bloqueado'?"Bloqueado":
                         bloqueioStatus.status==='pendente'?"Pendente":"Desbloqueado"}
                      </div>
                      {bloqueioStatus.confirmado_em&&(
                        <div style={{fontSize:9,color:"#475569"}}>
                          {new Date(bloqueioStatus.confirmado_em).toLocaleString('pt-BR')}
                        </div>
                      )}
                    </div>
                    {bloqueioStatus.status==='pendente'&&(
                      <span style={{fontSize:10,background:"#451a03",color:"#fbbf24",
                        padding:"2px 8px",borderRadius:8}}>Aguard. confirmação</span>
                    )}
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:6}}>
                    <button disabled={bloqueando||bloqueioStatus.status==='bloqueado'}
                      onClick={async()=>{
                        const ok=await confirm({mensagem:`Bloquear ${selecionado.nome}?`,tipo:'warning',labelConfirm:'Bloquear',labelCancel:'Cancelar'})
                        if(!ok) return
                        setBloqueando(true)
                        try {
                          const r=await axios.post(`${API}/ativos/${selecionado.id}/bloquear`)
                          toast(r.data.mensagem,'success')
                          const s=await axios.get(`${API}/ativos/${selecionado.id}/bloqueio-status`)
                          setBloqueioStatus(s.data)
                        } catch(e:any){ toast(e.response?.data?.erro||'Erro ao bloquear','error') }
                        finally { setBloqueando(false) }
                      }}
                      style={{padding:"9px",borderRadius:8,
                        border:`1px solid ${bloqueioStatus.status==='bloqueado'?"#2d4166":"#dc2626"}`,
                        background:"transparent",
                        color:bloqueioStatus.status==='bloqueado'?"#475569":"#f87171",
                        cursor:bloqueioStatus.status==='bloqueado'?"not-allowed":"pointer",
                        fontSize:12,fontWeight:600,fontFamily:"Inter",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {bloqueando ? <><i className="ph-bold ph-circle-notch" style={{fontSize:13,marginRight:4}}/>Enviando...</> : <><i className="ph-bold ph-lock" style={{fontSize:13,marginRight:4}}/>Bloquear</>}
                    </button>
                    <button disabled={bloqueando||bloqueioStatus.status==='desbloqueado'}
                      onClick={async()=>{
                        const ok2=await confirm({mensagem:`Desbloquear ${selecionado.nome}?`,tipo:'info',labelConfirm:'Desbloquear',labelCancel:'Cancelar'})
                        if(!ok2) return
                        setBloqueando(true)
                        try {
                          const r=await axios.post(`${API}/ativos/${selecionado.id}/desbloquear`)
                          toast(r.data.mensagem,'success')
                          const s=await axios.get(`${API}/ativos/${selecionado.id}/bloqueio-status`)
                          setBloqueioStatus(s.data)
                        } catch(e:any){ toast(e.response?.data?.erro||'Erro ao desbloquear','error') }
                        finally { setBloqueando(false) }
                      }}
                      style={{padding:"9px",borderRadius:8,
                        border:`1px solid ${bloqueioStatus.status==='desbloqueado'?"#2d4166":"#22c55e"}`,
                        background:bloqueioStatus.status==='desbloqueado'?"transparent":"#14532d",
                        color:bloqueioStatus.status==='desbloqueado'?"#475569":"#4ade80",
                        cursor:bloqueioStatus.status==='desbloqueado'?"not-allowed":"pointer",
                        fontSize:12,fontWeight:600,fontFamily:"Inter",display:"flex",alignItems:"center",justifyContent:"center"}}>
                      {bloqueando ? <><i className="ph-bold ph-circle-notch" style={{fontSize:13,marginRight:4}}/>Enviando...</> : <><i className="ph-bold ph-lock-open" style={{fontSize:13,marginRight:4}}/>Desbloquear</>}
                    </button>
                  </div>
                </div>
              )}

              {showReplay && (
                <div style={{ background:"#0d1b2e", border:"1px solid #7c3aed", borderRadius:10, padding:12 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:"#c4b5fd", marginBottom:10 }}>🎬 Replay de trajeto</div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label style={{fontSize:10}}>Data início</label>
                      <input type="date" value={replayInicio.split("T")[0]||replayInicio}
                        onChange={e=>setReplayInicio(e.target.value+"T"+replayHoraInicio)}
                        style={{fontSize:11,padding:"4px 8px"}} />
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label style={{fontSize:10}}>Hora início</label>
                      <input type="time" value={replayHoraInicio}
                        onChange={e=>{ setReplayHoraInicio(e.target.value); setReplayInicio((replayInicio.split("T")[0]||replayInicio)+"T"+e.target.value) }}
                        style={{fontSize:11,padding:"4px 8px"}} />
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label style={{fontSize:10}}>Data fim</label>
                      <input type="date" value={replayFim.split("T")[0]||replayFim}
                        onChange={e=>setReplayFim(e.target.value+"T"+replayHoraFim)}
                        style={{fontSize:11,padding:"4px 8px"}} />
                    </div>
                    <div className="form-group" style={{marginBottom:0}}>
                      <label style={{fontSize:10}}>Hora fim</label>
                      <input type="time" value={replayHoraFim}
                        onChange={e=>{ setReplayHoraFim(e.target.value); setReplayFim((replayFim.split("T")[0]||replayFim)+"T"+e.target.value) }}
                        style={{fontSize:11,padding:"4px 8px"}} />
                    </div>
                  </div>
                  <div style={{ display:"flex", gap:6 }}>
                    <button
                      onClick={()=>{
                        const dataInicio = replayInicio.includes("T") ? replayInicio : replayInicio+"T00:00:00"
                        const dataFim = replayFim.includes("T") ? replayFim : replayFim+"T23:59:59"
                        if (!replayInicio || !replayFim) return alert("Selecione data de início e fim")
                        window.dispatchEvent(new CustomEvent("navegarReplay", {
                          detail: { ativo_id: selecionado.id, inicio: dataInicio, fim: dataFim, nome: selecionado.nome }
                        }))
                      }}
                      style={{ flex:1, padding:"7px", borderRadius:8, border:"none", background:"#7c3aed", color:"#fff", cursor:"pointer", fontSize:12, fontWeight:600, fontFamily:"Inter" }}>
                      ▶ Ver trajeto
                    </button>
                    <button onClick={()=>{
                        const hoje = new Date().toISOString().split("T")[0]
                        setReplayHoraInicio("00:00")
                        setReplayHoraFim("23:59")
                        setReplayInicio(hoje+"T00:00"); setReplayFim(hoje+"T23:59")
                      }}
                      style={{ padding:"7px 10px", borderRadius:8, border:"1px solid #7c3aed", background:"transparent", color:"#c4b5fd", cursor:"pointer", fontSize:11, fontFamily:"Inter" }}>
                      Hoje
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {modalEventos && (
        <EventosModal ativoId={modalEventos.id} ativoNome={modalEventos.nome} onClose={()=>setModalEventos(null)} />
      )}
      <Toast toasts={toasts} onRemove={(id) => setToasts(prev => prev.filter(t => t.id !== id))} />
    </div>
  )
}
